import { call, put, select, takeEvery } from 'redux-saga/effects';

import { requestLifecycleFromType, ApiActions } from './actionService/actions';

import {
  showApiCallSpinner,
  hideApiCallSpinner,
  refreshUsersTokens,
  logoutSucces,
} from '../containers/App/actions';

import chooseRequest from './request/utils';
import { selectAccessToken, selectRefreshToken } from '../containers/App/selectors';
import apiService from './apiServices/apiService';
import { apiEndPoints } from '../config/endPoints/api';
import { createSessionRefresh } from './sessionRefresh';
import type { ApiFailure } from './types';

const ensureFreshSession = createSessionRefresh({
  selectAccessToken,
  selectRefreshToken,
  refresh: function* (token) {
    return yield call([apiService, apiService.refresh], token);
  },
  saveTokens: refreshUsersTokens,
  logout: logoutSucces,
});

function* requestAPI(action: ReturnType<typeof ApiActions.apiAction>) {
  const { payload } = action;

  const lifecycle = requestLifecycleFromType(action.type);
  yield put(ApiActions.pending(lifecycle)());

  const accessToken: ReturnType<typeof selectAccessToken> = yield select(selectAccessToken);
  const requestPayload = { ...payload, accessToken };
  const refreshToken: ReturnType<typeof selectRefreshToken> = yield select(selectRefreshToken);

  if (payload.showApiCallSpinner) {
    yield put(showApiCallSpinner());
  }

  try {
    const request = chooseRequest(payload.requestType);
    const result: unknown = yield call(request, requestPayload);
    yield put(ApiActions.resolve(lifecycle, result));
  } catch (_error) {
    const error = _error as ApiFailure;
    if (
      error.statusCode === 401 &&
      error.subcode === 1 &&
      !payload.upload?.signal?.aborted &&
      payload.controller !== apiEndPoints.auth.refresh
    ) {
      const refreshed: boolean = yield call(ensureFreshSession, accessToken, refreshToken);
      if (refreshed && !payload.upload?.signal?.aborted) {
        const nextAccessToken: ReturnType<typeof selectAccessToken> =
          yield select(selectAccessToken);
        try {
          const request = chooseRequest(payload.requestType);
          const result: unknown = yield call(request, {
            ...payload,
            accessToken: nextAccessToken,
          });
          yield put(ApiActions.resolve(lifecycle, result));
        } catch (retryError) {
          yield put(ApiActions.reject(lifecycle, retryError));
        }
      } else {
        yield put(
          ApiActions.reject(
            lifecycle,
            payload.upload?.signal?.aborted ? { statusCode: 0, code: 'ABORTED' } : error,
          ),
        );
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Api request failed', error);
      yield put(ApiActions.reject(lifecycle, error));
    }
  }

  if (payload.showApiCallSpinner) {
    yield put(hideApiCallSpinner());
  }
}

function* apiSaga() {
  // @ts-expect-error: saga types
  yield takeEvery((action: ReturnType<typeof ApiActions.apiAction>) => {
    return action.payload && action.payload.api;
  }, requestAPI);
}

export default apiSaga;
